import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import Button from '../../components/Button';
import AdminPageShell from './AdminPageShell';
import { ADMIN_NEXT_BASE, findAdminTool } from '../registry';

/**
 * Generic stand-in page for tools that haven't been rebuilt in the new suite
 * yet. Driven entirely by the registry: shows what the tool will consolidate
 * and links to the legacy implementation(s). When a tool is rebuilt, give it
 * a real page and route — this page stops matching automatically.
 */
const LegacyToolPage: React.FC = () => {
  const { toolKey } = useParams<{ toolKey: string }>();
  const tool = toolKey ? findAdminTool(toolKey) : undefined;

  if (!tool) {
    return <Navigate to={ADMIN_NEXT_BASE} replace />;
  }

  return (
    <AdminPageShell title={tool.title} description={tool.description}>
      <div className="card p-6">
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          This tool hasn't been rebuilt in the new admin suite yet. It will
          consolidate:{' '}
          <span className="font-medium text-text-primary dark:text-text-dark-primary">
            {tool.consolidates.join(' · ')}
          </span>
        </p>
        <p className="mt-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
          Until then, use the legacy version:
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {tool.legacyLinks.map((link) => (
            <Link key={link.path} to={link.path}>
              <Button variant="outline" size="sm" icon={ExternalLink} iconPosition="right">
                {link.label}
              </Button>
            </Link>
          ))}
        </div>
      </div>
    </AdminPageShell>
  );
};

export default LegacyToolPage;
